/**
 * @file requestCache.js
 * @description Shared in-memory request cache with TTL and in-flight deduplication.
 */

const responseCache = new Map();
const inFlightCache = new Map();

function buildCacheKey(namespace, key) {
    return `${namespace}:${key}`;
}

function getCachedResponse(cacheKey) {
    const entry = responseCache.get(cacheKey);
    if (!entry) {
        return undefined;
    }

    if (Date.now() > entry.expiresAt) {
        responseCache.delete(cacheKey);
        return undefined;
    }

    return entry.value;
}

function setCachedResponse(cacheKey, value, ttlMs) {
    if (!ttlMs || ttlMs <= 0) {
        return;
    }

    responseCache.set(cacheKey, {
        value,
        expiresAt: Date.now() + ttlMs,
    });
}

export async function getOrLoadRequestCache({ namespace, key, ttlMs, loader }) {
    const cacheKey = buildCacheKey(namespace, key);

    const cachedResponse = getCachedResponse(cacheKey);
    if (cachedResponse !== undefined) {
        return cachedResponse;
    }

    if (inFlightCache.has(cacheKey)) {
        return inFlightCache.get(cacheKey);
    }

    const requestPromise = Promise.resolve()
        .then(loader)
        .then(response => {
            setCachedResponse(cacheKey, response, ttlMs);
            return response;
        })
        .finally(() => {
            inFlightCache.delete(cacheKey);
        });

    inFlightCache.set(cacheKey, requestPromise);
    return requestPromise;
}

export function clearRequestCacheByNamespace(namespace) {
    const namespacePrefix = `${namespace}:`;

    Array.from(responseCache.keys()).forEach(cacheKey => {
        if (cacheKey.startsWith(namespacePrefix)) {
            responseCache.delete(cacheKey);
        }
    });

    Array.from(inFlightCache.keys()).forEach(cacheKey => {
        if (cacheKey.startsWith(namespacePrefix)) {
            inFlightCache.delete(cacheKey);
        }
    });
}
